import { Button, IconButton, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material';
import { ResponsePlaceDto } from 'features/place/types/dtos';
import { FormProps } from 'types/form';
import { Promotion } from '../types/dtos';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import OWForm from 'components/layout/ow-form';
import FormFooter from 'components/layout/form-footer';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'utils/object-helpers';
import OWSelect from 'components/design/ow-select';
import { MultiSelect } from 'components/design/multi-select';
import { ToolTip } from 'components/controls';
import { InfoOutlined } from '@mui/icons-material';
import { useFilterSelectOptions as useFilterPageSelectOptions } from '../constants/page-type';
import { ResponseProductDto } from 'features/product/types/dtos';
import { ProductConfigData } from '../controllers/add-promotion-controller';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PRODUCT_STATUS } from 'features/product/constants/status';
import { useFilterSelectOptions as useFilterTypeSelectOptions } from '../constants/type';

export type PromotionFormProps = FormProps<Promotion> & {
    places?: { content: ResponsePlaceDto[] };
    products?: { content: ResponseProductDto[] };
    isAdmin?: boolean;
    isEdit?: boolean;
    placeId: string;
    individualPrices?: boolean;
    onPlaceIdsSelectChange?: (event: SelectChangeEvent<string[]>) => void;
    onProductSelectChange?: (event: SelectChangeEvent<string[]>) => void;
    setIndividualPrices?: (value: boolean) => void;
    placeIds: string[];
    productConfigData: ProductConfigData[];
    onClearAllProducts?: (event: SelectChangeEvent<string[]>) => void;
    onClearProduct?: (event: SelectChangeEvent<string[]>) => void;
    onConfigPriceChange: (field: string, value: string, errorPrefix: string[], placeId?: string) => void;
};

type PromotionFormType = (props: PromotionFormProps) => ReactNode;

export const PromotionForm: PromotionFormType = ({
    formData: promotion,
    places,
    products,
    onChange,
    onSubmit,
    onCancel,
    isSaveLoading,
    errors,
    disableSubmit,
    placeIds,
    onPlaceIdsSelectChange,
    onProductSelectChange,
    productConfigData,
    onConfigPriceChange,
    isEdit,
}) => {
    const { t } = useTranslation();
    const FILTER_SELECT_OPTIONS_PAGE = useFilterPageSelectOptions();
    const FILTER_SELECT_OPTIONS_TYPE = useFilterTypeSelectOptions();

    return (
        <OWForm onSubmit={onSubmit}>
            <Typography variant="h6">{t('PROMOTION_FORM.HEADER')}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack width="100%" spacing={1.5}>
                    <TextField
                        value={promotion.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('PROMOTION_FORM.PLACEHOLDER_NAME')}
                        label={t('PROMOTION_FORM.LABEL_NAME')}
                        required
                        autoFocus
                        error={!!errors?.name}
                        helperText={errors?.name?.errorMessage}
                    />
                    <Stack direction="row" spacing={1.5}>
                        <OWSelect
                            value={promotion.page}
                            items={FILTER_SELECT_OPTIONS_PAGE}
                            label={t('PROMOTION_FORM.LABEL_PAGE')}
                            labelId="label-status"
                            onChange={(e) => onChange('page', e.target.value)}
                        />
                        <OWSelect
                            value={promotion.importance.toString()}
                            items={[
                                { label: '1000', value: '1000' },
                                { label: '2000', value: '2000' },
                            ]}
                            label={t('PROMOTION_FORM.LABEL_IMPORTANCE')}
                            labelId="label-status"
                            onChange={(e) => onChange('importance', e.target.value)}
                        />
                    </Stack>
                </Stack>
            </Stack>
            <MultiSelect
                items={places?.content}
                selectedIds={placeIds.map((id) => id)}
                onSelectChange={onPlaceIdsSelectChange ? onPlaceIdsSelectChange : () => {}}
                translationKey="PROMOTION_FORM.LABEL_PLACE"
                disabled={true}
                required
            />
            <MultiSelect
                items={products?.content.filter((product) => product.isAvailable === PRODUCT_STATUS.AVAILABLE)}
                selectedIds={productConfigData.map((config) => config.productId)}
                onSelectChange={onProductSelectChange ? onProductSelectChange : () => {}}
                translationKey="PROMOTION_FORM.LABEL_PRODUCT"
                required
            />
            <OWSelect
                value={promotion.type}
                items={FILTER_SELECT_OPTIONS_TYPE}
                label={t('PROMOTION_FORM.LABEL_TYPE')}
                labelId="label-status"
                disabled={isEdit}
                onChange={(e) => onChange('type', e.target.value)}
            />
            {promotion.type === 'DISCOUNTED' && productConfigData.length > 0 && (
                <React.Fragment>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={dayjs(promotion.startDate) || null}
                            disablePast={true}
                            onChange={(e) => {
                                if (e) {
                                    onChange('startDate', e.format('YYYY-MM-DD'));
                                }
                            }}
                            label={t('PROMOTION_FORM.LABEL_START_DATE')}
                        ></DatePicker>
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={dayjs(promotion.endDate) || null}
                            minDate={promotion.startDate ? dayjs(promotion.startDate).add(1, 'day') : undefined}
                            disablePast={true}
                            disabled={!promotion.startDate}
                            onChange={(e) => {
                                if (e) {
                                    onChange('endDate', e.format('YYYY-MM-DD'));
                                }
                            }}
                            label={t('PROMOTION_FORM.LABEL_END_DATE')}
                        ></DatePicker>
                    </LocalizationProvider>

                    {productConfigData.map((config) => (
                        <Stack key={config.productId} direction="column" spacing={1}>
                            <Typography>{config.name}</Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    value={config.price}
                                    onChange={() => () => {}}
                                    label={t('PROMOTION_FORM.LABEL_PRICE')}
                                    placeholder={t('PROMOTION_FORM.PLACEHOLDER_PRICE')}
                                    fullWidth
                                    disabled={true}
                                />
                                <Stack direction={'row'} spacing={1} width="100%">
                                    <TextField
                                        value={config.discountedPrice || ''}
                                        onChange={(e) =>
                                            onConfigPriceChange(
                                                'discountedPrice',
                                                e.target.value,
                                                ['discountedPrice-'],
                                                config.productId,
                                            )
                                        }
                                        label={t('PRODUCT_FORM.LABEL_DISCOUNTED_PRICE')}
                                        placeholder={t('PROMOTION_FORM.PLACEHOLDER_DISCOUNTED_PRICE')}
                                        fullWidth
                                        error={!!errors?.[`discountedPrice-${config.productId}`]}
                                        helperText={errors?.[`discountedPrice-${config.productId}`]?.errorMessage}
                                        sx={{ width: '100%' }}
                                    />
                                    <ToolTip title={t('PROMOTION_FORM.DISCOUNTED_PRICE_INFO')}>
                                        <IconButton sx={{ alignSelf: 'flex-start' }}>
                                            <InfoOutlined />
                                        </IconButton>
                                    </ToolTip>
                                </Stack>
                            </Stack>
                        </Stack>
                    ))}
                </React.Fragment>
            )}
            <FormFooter>
                <Stack direction="row" width="360px" spacing={3}>
                    <Button variant="outlined" onClick={onCancel}>
                        {t('PROMOTION_FORM.CANCEL_BUTTON')}
                    </Button>
                    <LoadingButton
                        loading={isSaveLoading}
                        type="submit"
                        disabled={!isEmpty(errors) || disableSubmit || !(productConfigData.length > 0)}
                    >
                        {t('PROMOTION_FORM.SAVE_BUTTON')}
                    </LoadingButton>
                </Stack>
            </FormFooter>
        </OWForm>
    );
};
